// Show content swiper bullets with posters when poster images are available
// Carousel functionality enabled when there are more than 5 bullets
// Only when on the variant the poster data is available.

import { trackAction } from 'helpers/trackingOM';
import { PeekAheadWatchlistCta } from './PeekAheadWatchlistCta';

let isHub =
    document.location.href.indexOf('/brands/') > 1 ||
    document.location.href.indexOf('/collections/') > 1;

export class MarqueePeekAhead {
    constructor() {
        this.params = {
            minBulletCount: 5,
            posterUrls: [],
            posterIds: [],
            posterLocks: [],
            singleDirection: true,
        };
        this.activeIndex = null;
        this.bulletSorting = [];
        this.cycleCount = 0;
        this.hasPosters = false;
        this.heroSelector = null;
        this.isCarousel = false;
        this.isDesktop = false;
        this.marqueeTitle = isHub ? 'Hub Marquee' : 'Home Marquee';
        this.myListCtas = [];
        this.myListCtasSlideIndexes = [];
        this.posterSizeFactor = 146 / 97;
        this.posterImages = [];
        this.prevActiveIndex = null;
        this.slides = null;
        this.slidesCount = 0;
        this.swiperInstance = null;
        this.visibleBullets = [];

        this.HeroSliderEl = null;
        this.SwpPagEl = null;
        this.SwpPgnWrapEl = null;
        this.SwpPgnPrevWrapEl = null;
        this.isFeatureEnabled = CBS.Registry.Features.marquee_peek_ahead_enabled;
    }

    initBulletCarousel(swiper, heroSelector) {
        if (!this.isFeatureEnabled) return false;

        this.heroSelector = heroSelector;
        this.swiperInstance = swiper;
        // get DOM wrappers
        this.SwpPagEl = document.querySelector('.swiper-pagination');
        this.SwpPgnWrapEl = document.querySelector('.swiper-pagination-wrapper');
        this.SwpPgnPrevWrapEl = document.querySelector('.swiper-pagination-prev-wrapper');
        this.HeroSliderEl = document.querySelector(this.heroSelector);

        // get DOM data
        const posterData = this.SwpPagEl?.getAttribute('data-posters');
        const posterIds = this.SwpPagEl?.getAttribute('data-contentIds');
        const posterLocks = this.SwpPagEl?.getAttribute('data-locks');
        const carouselSize = this.SwpPagEl?.getAttribute('data-carouselSize');
        this.isDesktop = this.SwpPgnWrapEl?.classList.contains('desktop');

        // Parse data
        if (posterData) {
            this.params.posterUrls = posterData.split('|');
            this.slidesCount = this.params.posterUrls.length;
            this.params.posterIds = posterIds.split('|');
            this.params.posterLocks = posterLocks.split('|');

            if (this.slidesCount <= 2) return false;

            //preload images
            this.#preloadPosters();

            // Determines carousel minimum size
            this.params.minBulletCount = Math.min(
                parseInt(carouselSize),
                this.slidesCount,
                this.params.minBulletCount,
            );

            this.hasPosters = this.slidesCount > 0;
            this.isCarousel = this.hasPosters && this.slidesCount > this.params.minBulletCount;

            // Generates initial visible bullet array
            this.visibleBullets = this.genVisibleBulletSequence(
                this.params.minBulletCount,
                this.slidesCount,
                0,
            );
        }

        if (!this.isCarousel) {
            document.querySelector('.hero-bullet-carousel-next').style.display = 'none';
            document.querySelector('.swiper-pagination-bullet-next-poster').style.display = 'none';
        }

        this.#initWatchListCtas();
        this.#initPagination();

        return this.hasPosters;
    }

    #initPagination() {
        // setup swiper
        const swiper = this.swiperInstance;
        swiper.params.speed = 50;
        this.activeIndex = swiper.realIndex;
        // Custom events
        swiper.on('slideChange', () => {
            this.#onSlideChange(
                swiper.slides,
                swiper.pagination,
                swiper.activeIndex,
                swiper.previousIndex,
            );
        });
        swiper.on('resize', () => {
            this.#handleSwiperResize();
        });

        // Set cycle count observer
        const heroSliderObserver = new MutationObserver((mutation) => {
            this.#updateCycleCount();
        });
        heroSliderObserver.observe(this.HeroSliderEl, {
            attributeFilter: ['data-cycle-count'],
            attributeOldValue: true,
            subtree: false,
        });

        this.#initBullets();

        this.#updateCarouselRightNav(this.visibleBullets.length);
        this.#updateCarouselLeftNav(this.visibleBullets[0]);

        this.#initEvents(swiper);

        this.#initTrackMarqueePosterActivePosterSelectEvent();

        // Initialize wrappers classes
        setTimeout(() => {
            if (this.isCarousel) {
                this.SwpPagEl.classList.add('carousel');
                if (!this.params.singleDirection) {
                    this.SwpPgnPrevWrapEl.classList.add('initialized');
                }
            }
            this.SwpPgnWrapEl.classList.add('initialized');
        }, 1000);

        // track marquee posters that are currently in view
        this.trackMarqueePosterView();
    }

    #initWatchListCtas() {
        try {
            const cta = document.querySelectorAll('.watchlist_wrapper');
            cta.forEach((element) => {
                if (
                    element.parentElement.parentElement.classList.contains(
                        'hero__content-slide-wrapper',
                    )
                ) {
                    this.myListCtas.push(new PeekAheadWatchlistCta(element));
                    // Record which slides have the cta
                    this.myListCtasSlideIndexes.push(
                        parseInt(element.parentElement.parentElement.getAttribute('aria-label')) -
                            1,
                    );
                }
            });
        } catch (e) {
            //
        }
    }

    #initEvents(swiper) {
        this.#bulletSelectEventSetup();

        // Carousel right arrow events
        if (this.isCarousel) {
            const nextArrow = document.querySelector('.hero-bullet-carousel-next');
            if (nextArrow) {
                nextArrow.addEventListener('click', () => {
                    // track next arrow click action
                    this.#trackMarqueePosterArrowSelect();
                    if (swiper.isEnd) {
                        swiper.slideTo(0);
                    } else {
                        swiper.slideNext();
                    }
                });
            }
            if (this.params.singleDirection === false) {
                // Carousel left arrow events
                const prevArrow = document.querySelectorAll('.hero-bullet-carousel-prev');
                if (prevArrow) {
                    prevArrow.forEach((arrow) => {
                        arrow.addEventListener('click', () => {
                            // track next arrow click action
                            this.#trackMarqueePosterArrowSelect();
                            if (!swiper.isBeginning) {
                                swiper.slidePrev();
                            }
                        });
                    });
                }
            }
        }
    }

    /**
     * @param slides
     * @param pagination
     * @param index
     * @param prevIndex
     */
    #onSlideChange(slides, pagination, index, prevIndex) {
        this.activeIndex = index;
        this.prevActiveIndex = prevIndex;
        this.slides = slides;
        // Determine direction
        let carouselReverse = index < prevIndex;
        if (carouselReverse && prevIndex - index !== 1) {
            carouselReverse = false;
        }
        // Add custom classes to slides and other elements

        Array.from(slides)?.forEach((slide) => {
            slide.classList.remove('from-prev');
            slide.classList.remove('from-next');
        });
        if (carouselReverse) {
            slides[index].classList.add('from-prev');
        } else {
            slides[index].classList.add('from-next');
        }

        if (index === 0) {
            slides[index].classList.add('first-slide');
            this.SwpPgnPrevWrapEl.classList.add('first-slide');
        } else {
            this.SwpPgnPrevWrapEl.classList.remove('first-slide');
        }

        if (this.isPeekAheadShowing()) {
            this.#refreshPaginationState(pagination, index, carouselReverse);
        }
    }

    /**
     * @param pagination
     * @param activeIndex
     * @param reverse
     * @private
     */
    #refreshPaginationState(pagination, activeIndex, reverse) {
        if (this.isCarousel && !this.params.singleDirection) {
            this.visibleBullets = this.genVisibleBulletSequence(
                this.params.minBulletCount,
                this.slidesCount,
                activeIndex,
            );
            const visiblePrevIndex = this.visibleBullets.shift();
            const nextIndex = this.visibleBullets[this.visibleBullets.length - 1];
            this.#updateCarouselRightNav(this.#calcNextIndex(nextIndex));
            this.#updateCarouselLeftNav(activeIndex);

            this.#refreshPaginationRender(pagination, activeIndex, visiblePrevIndex, reverse);
        }
        if (this.isCarousel && this.params.singleDirection) {
            if (!this.visibleBullets.includes(activeIndex)) {
                const nextIndex = this.#calcNextIndex(activeIndex);
                const visiblePrevIndex = this.visibleBullets.shift();
                this.visibleBullets.push(activeIndex);
                this.#updateCarouselRightNav(nextIndex);
                this.#refreshPaginationRender(pagination, activeIndex, visiblePrevIndex);
            }
        }
    }

    /**
     Render, animate, and arrange the bullets
     */
    #refreshPaginationRender(pagination, activeIndex, oldActiveIndex, reverse = false) {
        const hideClass = 'hide';
        const fadingClass = 'fading';
        const showClass = 'show';
        this.SwpPagEl.classList.remove('pagination-scroll-right-to-left');
        this.SwpPagEl.classList.remove('pagination-scroll-left-to-right');
        this.SwpPgnWrapEl.classList.remove('pagination-scroll-right-to-left');
        this.SwpPgnWrapEl.classList.remove('pagination-scroll-left-to-right');
        this.SwpPgnPrevWrapEl.classList.remove('pagination-scroll-right-to-left');
        this.SwpPgnPrevWrapEl.classList.remove('pagination-scroll-left-to-right');
        this.SwpPagEl.getBoundingClientRect();
        for (let i = 0; i < pagination.bullets.length; i++) {
            this.#removeClassesExcept(pagination.bullets[i], [
                'swiper-pagination-bullet',
                'swiper-pagination-bullet-active',
                'locked',
            ]);
            // add classes
            if (this.params.singleDirection) {
                if (this.visibleBullets.includes(i)) {
                    pagination.bullets[i].classList.add(showClass);
                } else {
                    if (i === oldActiveIndex) {
                        pagination.bullets[i].classList.add(fadingClass);
                    } else {
                        pagination.bullets[i].classList.add(hideClass);
                    }
                }
            } else {
                if (this.visibleBullets.includes(i)) {
                    pagination.bullets[i].classList.add(showClass);
                } else if (i === this.activeIndex && !reverse) {
                    pagination.bullets[i].classList.add(fadingClass);
                } else if (i === this.prevActiveIndex && reverse) {
                    pagination.bullets[i].classList.add(fadingClass);
                } else {
                    pagination.bullets[i].classList.add(hideClass);
                }
            }
        }

        this.SwpPagEl.classList.add(
            reverse ? 'pagination-scroll-left-to-right' : 'pagination-scroll-right-to-left',
        );
        this.SwpPgnWrapEl.classList.add(
            reverse ? 'pagination-scroll-left-to-right' : 'pagination-scroll-right-to-left',
        );
        this.SwpPgnPrevWrapEl.classList.add(
            reverse ? 'pagination-scroll-left-to-right' : 'pagination-scroll-right-to-left',
        );

        // arrange bullets
        this.#resetBulletsOrder(pagination, activeIndex, oldActiveIndex);
    }

    /**
     arrange and re-order bullets
     */
    #resetBulletsOrder(pagination, activeIndex, removedIndex = -1) {
        let nonSortedSequence = this.params.minBulletCount + 1;
        this.bulletSorting = [];
        if (this.params.singleDirection) {
            for (let i = 0; i < pagination.bullets.length; i++) {
                if (this.visibleBullets.indexOf(i) > -1) {
                    pagination.bullets[i].style.order = this.visibleBullets.indexOf(i) + 1;
                } else if (removedIndex === i) {
                    pagination.bullets[removedIndex].style.order = 0;
                } else {
                    pagination.bullets[i].style.order = nonSortedSequence;
                    nonSortedSequence++;
                }
                this.bulletSorting.push(pagination.bullets[i].style.order);
            }
        } else {
            const mixedBullets = [...this.visibleBullets];
            for (let i = 0; i < pagination.bullets.length; i++) {
                if (mixedBullets.includes(i)) {
                    pagination.bullets[i].style.order =
                        mixedBullets.indexOf(i) + this.params.minBulletCount - 1;
                } else if (activeIndex === i) {
                    pagination.bullets[activeIndex].style.order = this.params.minBulletCount - 2;
                } else {
                    pagination.bullets[i].style.order = 1;
                }

                this.bulletSorting.push(pagination.bullets[i].style.order);
            }
        }
    }

    getVisiblePosterIds() {
        let visibleIds = [];

        this.visibleBullets.map((value, index, array) => {
            if (index < array.length) {
                visibleIds.push(this.params.posterIds[value]);
            }
        });

        return visibleIds.join('|');
    }

    #updateCycleCount() {
        const heroDoc = document.querySelector('#hero-slider');
        this.cycleCount = heroDoc?.getAttribute('data-cycle-count');
        this.#spreadCycleCountState();
    }

    getCycleCount() {
        return this.cycleCount;
    }

    #spreadCycleCountState() {
        this.myListCtas.forEach((myListCta) => {
            myListCta.setSlideCycleState(this.getCycleCount());
        });
    }

    genVisibleBulletSequence(size, resetAt, startFrom) {
        const result = [];
        for (let i = startFrom; i < startFrom + size; i++) {
            result.push(i % resetAt);
        }
        return result;
    }

    #removeClassesExcept(element, classesToKeep) {
        const allClasses = element.classList;
        const classesArray = Array.from(allClasses);
        classesArray.forEach((className) => {
            if (!classesToKeep.includes(className)) {
                element.classList.remove(className);
            }
        });
    }

    #handleSwiperResize() {
        this.#adjustElementsRendering();
    }

    #adjustElementsRendering() {
        // set position of the hero volume and hero volume background based on the size of the swiper bullet
        const heroVolumeOffset = 20;
        const heroVolumeBackgroundOffset = 45;
        const sizeFactor = this.posterSizeFactor;
        const swiperPaginationBulletSize = document.querySelector(
            '.swiper-pagination-bullet img',
        )?.clientWidth;
        const heroVolume = document.querySelector('.hero-volume');
        const heroVolumeBackground = document.querySelector('.hero-volume-background');
        const heroBulletCarouselNext = document.querySelector('.hero-bullet-carousel-next');
        const heroPaginationWrapper = document.querySelector('.swiper-pagination-wrapper');
        if (heroVolume && heroVolumeBackground && swiperPaginationBulletSize > 0) {
            heroVolume.style.bottom = `${swiperPaginationBulletSize * sizeFactor + heroVolumeOffset}px`;
            heroVolumeBackground.style.top = `-${swiperPaginationBulletSize * sizeFactor + heroVolumeOffset + heroVolumeBackgroundOffset}px`;
            heroBulletCarouselNext.style.height = `${swiperPaginationBulletSize * sizeFactor}px`;
            heroPaginationWrapper.style.height = `${swiperPaginationBulletSize * sizeFactor}px`;
        }
    }

    #updateCarouselRightNav(index) {
        let paginationNextBulletImage = document.querySelector(
            '.swiper-pagination-bullet-next-poster i img',
        );
        const newNode = this.posterImages[index]?.cloneNode();
        if (newNode) {
            newNode.onload = () => {
                newNode.classList.add('loaded');
            };
            paginationNextBulletImage.replaceWith(newNode);
        }
    }

    #updateCarouselLeftNav(index) {
        let paginationPosterImage = document.querySelector(
            '.swiper-pagination-prev-wrapper .poster-selector a img',
        );
        const newNode = this.posterImages[index]?.cloneNode();
        if (newNode) {
            newNode.onload = () => {
                newNode.classList.add('loaded');
            };
            paginationPosterImage.replaceWith(newNode);
        }

        let paginationPrevPosterImage = document.querySelector(
            '.swiper-pagination-prev-wrapper .prev-poster-selector img',
        );

        const newPrevNode =
            index === 0 ? document.createElement('img') : this.posterImages[index - 1]?.cloneNode();

        if (newPrevNode) {
            newPrevNode.onload = () => {
                newPrevNode.classList.add('loaded');
            };
            paginationPrevPosterImage.replaceWith(newPrevNode);
        }

        // Poster selector events
        const heroCtas = document.querySelectorAll(
            '.hero__content-slide-wrapper .cta-box a.button',
        );
        if (heroCtas) {
            heroCtas.forEach((ctaLink, ctaIndex) => {
                if (index === ctaIndex) {
                    const poster = document.querySelector(
                        '.swiper-pagination-prev-wrapper .poster-selector',
                    );
                    const posterLink = document.querySelector(
                        '.swiper-pagination-prev-wrapper .poster-selector a',
                    );
                    const slide = document.querySelector('.swiper-slide-active');
                    posterLink?.setAttribute('href', ctaLink?.getAttribute('href'));
                    posterLink?.setAttribute('aa-link', ctaLink?.getAttribute('aa-link'));
                    posterLink?.setAttribute('data-tracking', slide?.getAttribute('data-tracking'));
                    posterLink?.setAttribute('data-pos', index);
                    // update lock status
                    if (this.params.posterLocks[index] === '1') {
                        poster?.classList.add('locked');
                    } else {
                        poster?.classList.remove('locked');
                    }
                }
            });
        }
    }

    #calcNextIndex(activeIndex) {
        return (activeIndex + 1) % this.slidesCount;
    }

    #preloadPosters() {
        this.params.posterUrls.forEach((url) => {
            let img = document.createElement('img');
            img.src = url;
            img.onload = () => {
                img.classList.add('loaded');
            };
            this.posterImages.push(img);
        });
    }

    #initBullets() {
        const paginationNextBulletImages = document.querySelectorAll(
            'span.swiper-pagination-bullet',
        );
        paginationNextBulletImages.forEach((bullet, index) => {
            // Add Poster
            const bulletI = bullet.querySelector('i');
            const newNode = this.posterImages[index]?.cloneNode();
            if (bulletI && newNode) {
                newNode.onload = () => {
                    newNode.classList.add('loaded');
                };
                bulletI.append(newNode);

                // Add lock gradient
                const gradient = document.createElement('span');
                gradient.classList.add('locked-gradient');
                bulletI.append(gradient);

                // Add lock container
                const lock = document.createElement('span');
                lock.classList.add('locked-content');
                bullet.append(lock);

                // Add initial classes
                if (
                    index >= this.params.minBulletCount ||
                    (index === 0 && !this.params.singleDirection)
                ) {
                    bullet.classList.add('hide');
                } else {
                    bullet.classList.add('initialized');
                }
                if (this.params.posterLocks[index] === '1') {
                    bullet.classList.add('locked');
                }
            }
        });
    }

    #bulletSelectEventSetup() {
        document.querySelectorAll('.swiper-pagination-bullet').forEach((element) => {
            element.addEventListener('click', (e) => {
                let trackingData = element.getAttribute('data-tracking');
                let posColNum = element.getAttribute('data-pos');
                this.#trackMarqueeSelection(trackingData, posColNum);
            });
        });
    }

    #trackMarqueeSelection(data, posColNum, customAction = null) {
        let action = customAction ? customAction : 'trackMarqueePosterSelect';
        let bundleStatus = CBS.Registry.user['bundleStatus']
            ? CBS.Registry.user['bundleStatus']
            : '';
        let dd = data.split('|');
        let _screenName = $('#global-header-container .user-profile-name').text() ?? '';
        let spliceEnabled = $('.hero__wrapper .swiper-slide-active .marqueeVideo').length
            ? '1'
            : '0';

        if (dd !== undefined) {
            let isMovie = dd[5] === 'movie';

            let obj = {
                liveTvChannel: 'undefined',
                slideId: dd[12],
                slideTitle: dd[13],
                slideTarget: dd[14],
                slideCTA: dd[3],
                slideType: dd[15] ?? '',
                rowHeaderTitle: isHub ? 'Hub Marquee' : 'Home Marquee',
                posRowNum: '0',
                posColNum: posColNum.toString(),
                slideCycle: this.cycleCount.toString(),
                showSectionTitle: !isMovie ? 'home' : '',
                showSeriesId: !isMovie ? dd[6] : '',
                showSeriesTitle: !isMovie ? dd[13] : '',
                showGenre: !isMovie ? dd[11] : '',
                screenName: _screenName,
                contentBrand: dd[8] ?? '',
                movieSectionTitle: isMovie ? 'home' : '',
                movieId: isMovie || dd[6] === '61457287' ? dd[9] : '',
                movieTitle: isMovie ? dd[10] : '',
                movieGenre: isMovie ? dd[11] : '',
                spliceEnabled: spliceEnabled,
                contentLocked: dd[17],
                bundleStatus: bundleStatus,
                videoPreviewId: dd[18],
                dma: dd[19],
                isPvr: dd[20] ?? '',
                marqueePostersContentList: this.getVisiblePosterIds() ?? '',
                myListEnabled:
                    this.myListCtasSlideIndexes.indexOf(this.activeIndex) > -1 ? '1' : '0',
            };

            trackAction(action, obj);
        }
    }

    /**
     * track when user clicks arrow to scroll posters in peek ahead variant
     */
    #trackMarqueePosterArrowSelect() {
        let action = 'trackMarqueePosterArrowSelect';
        let obj = {
            rowHeaderTitle: this.marqueeTitle,
            posRowNum: '0',
            marqueePostersContentList: this.getVisiblePosterIds() ?? '',
        };

        trackAction(action, obj);
    }

    /**
     * track which marquee posters are currently displayed in peek ahead variant
     */
    trackMarqueePosterView() {
        const activeSlide = document.querySelector('.swiper-slide-active');
        const trackingData = activeSlide?.getAttribute('data-tracking') ?? '';
        const posColNum = activeSlide?.getAttribute('data-pos') ?? '0';
        this.#trackMarqueeSelection(
            trackingData,
            posColNum,
            isHub ? 'trackHubMarqueeView' : 'trackMarqueeView',
        );
    }

    getMarqueePosterViewTrackingData() {
        if (!this.isFeatureEnabled) return {};
        let posterIds = this.params.posterIds;
        posterIds = this.getVisiblePosterIds();
        return {
            rowHeaderTitle: this.marqueeTitle,
            posRowNum: '0',
            marqueePostersContentList: posterIds ?? '',
            myListEnabled: this.myListCtasSlideIndexes.indexOf(this.activeIndex) > -1 ? '1' : '0',
        };
    }

    #initTrackMarqueePosterActivePosterSelectEvent() {
        const posterLink = document.querySelector(
            '.swiper-pagination-prev-wrapper .poster-selector a',
        );
        posterLink?.addEventListener('click', (e) => {
            let trackingData = posterLink.getAttribute('data-tracking');
            let posColNum = posterLink.getAttribute('data-pos');
            this.#trackMarqueeSelection(trackingData, posColNum, 'trackMarqueeActivePosterSelect');
        });
    }

    #initTrackMarqueCtaSelect() {
        const heroCtas = document.querySelectorAll(
            '.hero__content-slide-wrapper .cta-box a.button',
        );
        if (heroCtas) {
            heroCtas.forEach((ctaLink, ctaIndex) => {
                ctaLink.addEventListener('click', (e) => {
                    let trackingData = ctaLink.getAttribute('data-tracking');
                    let posColNum = ctaLink.getAttribute('data-pos');
                    this.#trackMarqueeSelection(trackingData, posColNum, 'trackMarqueeSelect');
                });
            });
        }
    }

    isPeekAheadShowing() {
        let windowWidth = this._isSafari ? $(window).width() : $(window).outerWidth();
        return windowWidth >= 1024;
    }
}
